import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Users,
  Package,
  Coins,
  ShoppingCart,
  Wallet,
  UserPlus,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react';
import dashboardService, {
  CustomerArticleStats,
  CustomerReportFilters,
  CustomerReportResponse,
  CustomerReportSortBy,
  CustomerReportStatoPagamento,
  DateRangeFilter,
} from '../../services/dashboardService';
import categorieArticoliService, { CategoriaArticolo } from '../../services/categorieArticoliService';
import rubricaService from '../../services/rubricaService';
import { SearchableSelect, SelectOption } from '../../components/common/SearchableSelect';
import { Articolo, ContattoRubrica } from '../../types';
import { useTipiContatto } from '@/hooks/useTipiContatto';
import styles from './ReportPage.module.css';

interface CustomerReportSectionProps {
  dateRange: DateRangeFilter;
  articoli: Articolo[];
  province: string[];
  /** Filtro articolo controllato dal parent (permette il salto da "Articoli Venduti") */
  articoloId: number | null;
  onArticoloIdChange: (articoloId: number | null) => void;
  /** Filtro provincia controllato dal parent (permette il salto da "Vendite per Provincia") */
  provincia: string;
  onProvinciaChange: (provincia: string) => void;
  /** Notifica i dati al parent per l'esportazione Excel/PDF */
  onDataLoaded?: (data: CustomerReportResponse | null) => void;
}

const SORT_OPTIONS: Array<{ value: CustomerReportSortBy; label: string }> = [
  { value: 'TOTALE', label: 'Fatturato' },
  { value: 'ORDINI', label: 'N. movimenti' },
  { value: 'QUANTITA', label: 'Quantità acquistata' },
  { value: 'TICKET_MEDIO', label: 'Valore medio movimento' },
  { value: 'ULTIMO_ACQUISTO', label: 'Ultimo acquisto' },
  { value: 'NOME', label: 'Nome (A-Z)' },
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString('it-IT') : '-');

const formatNumber = (value: number) =>
  (value || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });

export const CustomerReportSection: React.FC<CustomerReportSectionProps> = ({
  dateRange,
  articoli,
  province,
  articoloId,
  onArticoloIdChange,
  provincia,
  onProvinciaChange,
  onDataLoaded,
}) => {
  // Tipi contatto configurabili dall'utente (pagina "Tipi Contatto")
  const { tipi: tipiContatto, label: tipoContattoLabel } = useTipiContatto();

  const [clienteId, setClienteId] = useState<number | null>(null);
  const [categoriaArticoloId, setCategoriaArticoloId] = useState<number | null>(null);
  const [tipoContatto, setTipoContatto] = useState<string>('');
  const [statoPagamento, setStatoPagamento] = useState<CustomerReportStatoPagamento>('TUTTI');
  const [sortBy, setSortBy] = useState<CustomerReportSortBy>('TOTALE');
  const [limit, setLimit] = useState<number>(10);

  const [categorieArticoli, setCategorieArticoli] = useState<CategoriaArticolo[]>([]);
  const [contatti, setContatti] = useState<ContattoRubrica[]>([]);
  const [data, setData] = useState<CustomerReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedClienteId, setExpandedClienteId] = useState<number | null>(null);

  const { startDate, endDate } = dateRange;

  useEffect(() => {
    let attivo = true;
    categorieArticoliService
      .getCategorieArticoli()
      .then((categorie) => {
        if (attivo) setCategorieArticoli(categorie);
      })
      .catch((err) => console.error('Error loading categorie articoli:', err));
    rubricaService
      .getContatti()
      .then((lista) => {
        if (attivo) setContatti(lista);
      })
      .catch((err) => console.error('Error loading contatti rubrica:', err));
    return () => {
      attivo = false;
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: CustomerReportFilters = {
        clienteId: clienteId || undefined,
        articoloId: articoloId || undefined,
        categoriaArticoloId: categoriaArticoloId || undefined,
        provincia: provincia || undefined,
        tipoContatto: tipoContatto || undefined,
        statoPagamento,
        sortBy,
        limit,
      };

      const response = await dashboardService.getCustomerReport(filters, { startDate, endDate });
      setData(response);
      onDataLoaded?.(response);
    } catch (err) {
      console.error('Error loading customer report:', err);
      setError('Errore nel caricamento del report clienti');
      setData(null);
      onDataLoaded?.(null);
    } finally {
      setLoading(false);
    }
    // onDataLoaded è volutamente escluso: è una callback del parent ricreata a ogni render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, clienteId, articoloId, categoriaArticoloId, provincia, tipoContatto, statoPagamento, sortBy, limit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Il dettaglio "chi ha comprato che cosa" arriva già con la risposta: raggruppo per cliente
  const articoliPerCliente = useMemo(() => {
    const mappa = new Map<number, CustomerArticleStats[]>();
    (data?.dettaglioArticoli || []).forEach((riga) => {
      const righe = mappa.get(riga.clienteId) || [];
      righe.push(riga);
      mappa.set(riga.clienteId, righe);
    });
    return mappa;
  }, [data]);

  const articoloSelezionato = useMemo(
    () => articoli.find((a) => a.id === articoloId) || null,
    [articoli, articoloId]
  );

  const clienteSelezionato = useMemo(
    () => contatti.find((c) => c.id === clienteId) || null,
    [contatti, clienteId]
  );

  // Lista clienti ricercabile: la rubrica può contenere centinaia di contatti
  const clientiOptions: SelectOption[] = useMemo(
    () =>
      contatti.map((contatto) => {
        const codice = contatto.codiceProgressivo || '';
        const zona = [contatto.citta, contatto.provincia ? `(${contatto.provincia})` : '']
          .filter(Boolean)
          .join(' ');
        return {
          value: contatto.id,
          label: codice ? `[${codice}] ${contatto.nome}` : contatto.nome,
          subLabel: [tipoContattoLabel(contatto.tipoContatto), zona].filter(Boolean).join(' · '),
          searchTerms: [contatto.nome, codice, contatto.citta || '', contatto.provincia || '',
            contatto.email || '', contatto.telefono || ''].filter(Boolean).join(' '),
        };
      }),
    [contatti]
  );

  // Lista articoli ricercabile per nome, codice/barcode e merceologia
  const articoliOptions: SelectOption[] = useMemo(
    () =>
      articoli.map((articolo) => ({
        value: articolo.id,
        label: articolo.codice ? `${articolo.codice} - ${articolo.nome}` : articolo.nome,
        subLabel: articolo.categoriaArticoloNome || undefined,
        searchTerms: [articolo.nome, articolo.codice || '', articolo.categoriaArticoloNome || '']
          .filter(Boolean)
          .join(' '),
      })),
    [articoli]
  );

  const filtriAttivi =
    !!clienteId ||
    !!articoloId ||
    !!categoriaArticoloId ||
    !!provincia ||
    !!tipoContatto ||
    statoPagamento !== 'TUTTI';

  const resetFiltri = () => {
    setClienteId(null);
    onArticoloIdChange(null);
    setCategoriaArticoloId(null);
    onProvinciaChange('');
    setTipoContatto('');
    setStatoPagamento('TUTTI');
  };

  const clienti = useMemo(() => data?.clienti || [], [data]);
  const summary = data?.summary || null;

  // Filtrando su un singolo cliente il dettaglio "che cosa ha comprato" è il dato interessante:
  // apro subito la riga invece di costringere a un click in più
  useEffect(() => {
    if (clienteId) {
      setExpandedClienteId(clienteId);
    }
  }, [clienteId]);

  const barChartData = useMemo(() => {
    const top = clienti.slice(0, 10);
    return {
      labels: top.map((c) => (c.clienteNome.length > 28 ? `${c.clienteNome.slice(0, 28)}…` : c.clienteNome)),
      datasets: [
        {
          label: 'Fatturato',
          data: top.map((c) => c.totale),
          backgroundColor: 'rgba(33, 150, 243, 0.7)',
          borderColor: 'rgba(33, 150, 243, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [clienti]);

  const barChartOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: { dataIndex: number }) => {
              const cliente = clienti[context.dataIndex];
              if (!cliente) return '';
              return [
                `Fatturato: ${formatCurrency(cliente.totale)}`,
                `Movimenti: ${cliente.numeroMovimenti}`,
                `Valore medio: ${formatCurrency(cliente.scontrinoMedio)}`,
                cliente.articoloTopNome ? `Articolo top: ${cliente.articoloTopNome}` : '',
              ].filter(Boolean);
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value: string | number) =>
              formatCurrency(typeof value === 'number' ? value : parseFloat(value)),
          },
        },
      },
    }),
    [clienti]
  );

  const toggleCliente = (id: number) => {
    setExpandedClienteId((corrente) => (corrente === id ? null : id));
  };

  return (
    <div className={styles.soldArticlesSection}>
      <div className={styles.soldArticlesHeader}>
        <h3 className={styles.soldArticlesTitle}>
          <Users size={20} />
          {clienteSelezionato
            ? `Cliente: ${clienteSelezionato.nome}`
            : articoloSelezionato
              ? `Clienti di: ${articoloSelezionato.nome}`
              : 'Report Clienti'}
        </h3>
        <select
          className={styles.limitSelect}
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          aria-label="Numero di clienti da mostrare"
        >
          <option value={10}>Top 10</option>
          <option value={20}>Top 20</option>
          <option value={50}>Top 50</option>
          <option value={999999}>Tutti</option>
        </select>
      </div>

      {/* Barra filtri */}
      <div className={styles.filtersBar}>
        <div className={`${styles.filterField} ${styles.filterFieldWide}`}>
          <label>Cliente</label>
          <SearchableSelect
            options={clientiOptions}
            value={clienteId}
            onChange={(value) => setClienteId(value != null ? Number(value) : null)}
            placeholder="Tutti i clienti"
          />
        </div>

        <div className={`${styles.filterField} ${styles.filterFieldWide}`}>
          <label>Articolo</label>
          <SearchableSelect
            options={articoliOptions}
            value={articoloId}
            onChange={(value) => onArticoloIdChange(value != null ? Number(value) : null)}
            placeholder="Tutti gli articoli"
          />
        </div>

        <div className={styles.filterField}>
          <label htmlFor="filtro-merceologia">Merceologia</label>
          <select
            id="filtro-merceologia"
            value={categoriaArticoloId ?? ''}
            onChange={(e) => setCategoriaArticoloId(e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">Tutte</option>
            {categorieArticoli.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nome}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterField}>
          <label htmlFor="filtro-provincia">Provincia</label>
          <select id="filtro-provincia" value={provincia} onChange={(e) => onProvinciaChange(e.target.value)}>
            <option value="">Tutte</option>
            {province.map((prov) => (
              <option key={prov} value={prov}>
                {prov}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterField}>
          <label htmlFor="filtro-tipo-contatto">Tipo contatto</label>
          <select
            id="filtro-tipo-contatto"
            value={tipoContatto}
            onChange={(e) => setTipoContatto(e.target.value)}
          >
            <option value="">Tutti</option>
            {tipiContatto.map((tipo) => (
              <option key={tipo.value} value={tipo.value}>
                {tipo.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterField}>
          <label htmlFor="filtro-stato-pagamento">Stato incasso</label>
          <select
            id="filtro-stato-pagamento"
            value={statoPagamento}
            onChange={(e) => setStatoPagamento(e.target.value as CustomerReportStatoPagamento)}
          >
            <option value="TUTTI">Tutti</option>
            <option value="PAGATI">Solo incassati</option>
            <option value="NON_PAGATI">Solo da incassare</option>
          </select>
        </div>

        <div className={styles.filterField}>
          <label htmlFor="filtro-ordinamento">Ordina per</label>
          <select
            id="filtro-ordinamento"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as CustomerReportSortBy)}
          >
            {SORT_OPTIONS.map((opzione) => (
              <option key={opzione.value} value={opzione.value}>
                {opzione.label}
              </option>
            ))}
          </select>
        </div>

        {filtriAttivi && (
          <button type="button" className={styles.resetFiltersBtn} onClick={resetFiltri}>
            <X size={14} />
            Azzera filtri
          </button>
        )}
      </div>

      {clienteSelezionato && (
        <div className={styles.filterBanner}>
          <Filter size={16} />
          <span>
            Stai analizzando <strong>che cosa ha acquistato {clienteSelezionato.nome}</strong>
            {articoloSelezionato
              ? ` limitatamente all'articolo ${articoloSelezionato.nome}: il dettaglio è aperto sotto la riga del cliente.`
              : ': il dettaglio per articolo e per reparto (voci senza articolo, es. spedizioni) è aperto sotto la riga del cliente.'}
          </span>
        </div>
      )}

      {!clienteSelezionato && articoloSelezionato && (
        <div className={styles.filterBanner}>
          <Filter size={16} />
          <span>
            Stai analizzando <strong>chi ha acquistato {articoloSelezionato.nome}</strong>: importi e
            quantità in tabella si riferiscono solo a questo articolo.
          </span>
        </div>
      )}

      {loading && <div className={styles.sectionLoading}>Caricamento report clienti…</div>}

      {!loading && error && (
        <div className={styles.sectionError}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {!loading && !error && clienti.length === 0 && (
        <div className={styles.sectionEmpty}>Nessun cliente corrisponde ai filtri selezionati.</div>
      )}

      {!loading && !error && summary && clienti.length > 0 && (
        <>
          {/* Cards riepilogative */}
          <div className={styles.soldArticlesSummaryGrid}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <Users size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Clienti attivi</span>
                <span className={styles.summaryValue}>{summary.clientiAttivi}</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <UserPlus size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Nuovi clienti</span>
                <span className={styles.summaryValue}>{summary.nuoviClienti}</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <Coins size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Fatturato</span>
                <span className={styles.summaryValue}>{formatCurrency(summary.fatturatoTotale)}</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <ShoppingCart size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Movimenti</span>
                <span className={styles.summaryValue}>{summary.ordiniTotali}</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <Wallet size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Valore medio movimento</span>
                <span className={styles.summaryValue}>{formatCurrency(summary.ticketMedio)}</span>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryIcon}>
                <AlertCircle size={20} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Da incassare</span>
                <span className={styles.summaryValue}>{formatCurrency(summary.totaleDaIncassare)}</span>
              </div>
            </div>
          </div>

          {/* Indicatori statistici */}
          <div className={styles.insightsRow}>
            <div className={styles.insightItem}>
              <span className={styles.insightLabel}>Cliente principale</span>
              <span className={styles.insightValue}>
                {summary.clienteTopNome || '-'}
                {summary.clienteTopNome && ` · ${formatCurrency(summary.clienteTopTotale)}`}
              </span>
            </div>
            <div className={styles.insightItem}>
              <span className={styles.insightLabel}>Concentrazione primi 5 clienti</span>
              <span className={styles.insightValue}>{summary.concentrazioneTop5.toFixed(1)}% del fatturato</span>
            </div>
            <div className={styles.insightItem}>
              <span className={styles.insightLabel}>Fatturato medio per cliente</span>
              <span className={styles.insightValue}>{formatCurrency(summary.fatturatoMedioPerCliente)}</span>
            </div>
            <div className={styles.insightItem}>
              <span className={styles.insightLabel}>Movimenti medi per cliente</span>
              <span className={styles.insightValue}>{formatNumber(summary.ordiniMediPerCliente)}</span>
            </div>
          </div>

          {/* Tabella clienti con drill-down */}
          <div className={styles.soldArticlesTableWrapper}>
            <table className={styles.soldArticlesTable}>
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>#</th>
                  <th>Cliente</th>
                  <th className={styles.hideOnMobile}>Zona</th>
                  <th style={{ textAlign: 'right' }}>Mov.</th>
                  <th style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                    Qtà
                  </th>
                  <th className={styles.hideOnMobile}>Articolo più acquistato</th>
                  <th style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                    Valore medio
                  </th>
                  <th className={styles.hideOnMobile}>Ultimo acquisto</th>
                  <th style={{ textAlign: 'right' }}>Totale</th>
                  <th style={{ textAlign: 'right' }}>%</th>
                  <th style={{ width: '40px' }} aria-label="Dettaglio" />
                </tr>
              </thead>
              <tbody>
                {clienti.map((cliente, index) => {
                  const espanso = expandedClienteId === cliente.clienteId;
                  const righeArticoli = articoliPerCliente.get(cliente.clienteId) || [];
                  return (
                    <React.Fragment key={cliente.clienteId}>
                      <tr
                        className={`${index < 3 ? styles.topRanked : ''} ${styles.clickableRow}`}
                        onClick={() => toggleCliente(cliente.clienteId)}
                      >
                        <td style={{ fontWeight: 600, textAlign: 'center' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && index + 1}
                        </td>
                        <td>
                          <div className={styles.clienteCell}>
                            <span className={styles.clienteNome}>{cliente.clienteNome}</span>
                            <span className={styles.clienteMeta}>
                              {cliente.codiceProgressivo ? `${cliente.codiceProgressivo} · ` : ''}
                              {tipoContattoLabel(cliente.tipoContatto)}
                            </span>
                          </div>
                          {cliente.nuovoCliente && <span className={styles.badgeNuovo}>Nuovo</span>}
                          {cliente.totaleDaIncassare > 0 && (
                            <span className={styles.badgeDaIncassare}>
                              {formatCurrency(cliente.totaleDaIncassare)} da incassare
                            </span>
                          )}
                        </td>
                        <td className={styles.hideOnMobile}>
                          {cliente.citta || cliente.provincia
                            ? `${cliente.citta || ''}${cliente.citta && cliente.provincia ? ' ' : ''}${
                                cliente.provincia ? `(${cliente.provincia})` : ''
                              }`
                            : '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{cliente.numeroMovimenti}</td>
                        <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                          {formatNumber(cliente.quantitaTotale)}
                        </td>
                        <td className={styles.hideOnMobile}>
                          {cliente.articoloTopNome ? (
                            <div className={styles.articoloTopCell}>
                              <Package size={14} style={{ color: 'var(--primary-main)', flexShrink: 0 }} />
                              <span>{cliente.articoloTopNome}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                          {formatCurrency(cliente.scontrinoMedio)}
                        </td>
                        <td className={styles.hideOnMobile}>
                          {formatDate(cliente.ultimoAcquisto)}
                          {cliente.giorniDaUltimoAcquisto != null && (
                            <span className={styles.clienteMeta}>
                              {cliente.giorniDaUltimoAcquisto === 0
                                ? 'oggi'
                                : `${cliente.giorniDaUltimoAcquisto} gg fa`}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--success-main)' }}>
                          {formatCurrency(cliente.totale)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                          {cliente.percentualeSulTotale.toFixed(1)}%
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {espanso ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                      </tr>
                      {espanso && (
                        <tr className={styles.detailRow}>
                          <td colSpan={11}>
                            <div className={styles.detailPanel}>
                              <div className={styles.detailInfo}>
                                {cliente.email && (
                                  <span>
                                    <Mail size={14} /> {cliente.email}
                                  </span>
                                )}
                                {cliente.telefono && (
                                  <span>
                                    <Phone size={14} /> {cliente.telefono}
                                  </span>
                                )}
                                {(cliente.citta || cliente.provincia) && (
                                  <span>
                                    <MapPin size={14} /> {cliente.citta || ''}
                                    {cliente.provincia ? ` (${cliente.provincia})` : ''}
                                  </span>
                                )}
                                <span>Primo acquisto: {formatDate(cliente.primoAcquisto)}</span>
                                <span>Articoli diversi: {cliente.articoliDistinti}</span>
                                <span>Incassato: {formatCurrency(cliente.totaleIncassato)}</span>
                                <span>Da incassare: {formatCurrency(cliente.totaleDaIncassare)}</span>
                              </div>

                              {righeArticoli.length > 0 ? (
                                <table className={styles.detailTable}>
                                  <thead>
                                    <tr>
                                      <th>Articolo / Voce</th>
                                      <th>Merceologia</th>
                                      <th style={{ textAlign: 'right' }}>Quantità</th>
                                      <th style={{ textAlign: 'right' }}>Prezzo medio</th>
                                      <th style={{ textAlign: 'right' }}>Totale</th>
                                      <th style={{ textAlign: 'right' }}>Mov.</th>
                                      <th>Ultimo acquisto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {righeArticoli.map((riga) => (
                                      <tr
                                        key={
                                          riga.voceReparto
                                            ? `${riga.clienteId}-rep-${riga.repartoId ?? 'none'}`
                                            : `${riga.clienteId}-${riga.articoloId}`
                                        }
                                      >
                                        <td>
                                          {riga.articoloCodice ? `${riga.articoloCodice} - ` : ''}
                                          {riga.articoloNome}
                                          {riga.voceReparto && (
                                            <span className={styles.badgeReparto} title="Incassi senza articolo, raggruppati per reparto">
                                              Reparto
                                            </span>
                                          )}
                                        </td>
                                        <td>{riga.categoriaArticoloNome || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>
                                          {riga.voceReparto ? '-' : `${formatNumber(riga.quantita)} ${riga.unitaMisura}`}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                          {riga.voceReparto ? '-' : formatCurrency(riga.prezzoMedio)}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 500 }}>
                                          {formatCurrency(riga.ricavo)}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{riga.numeroMovimenti}</td>
                                        <td>{formatDate(riga.ultimoAcquisto)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className={styles.sectionEmpty}>
                                  Nessun incasso per questo cliente nel periodo.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Top 10 Clienti per Fatturato</h3>
            <div className={styles.chartContainer}>
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerReportSection;
